import { CompanyType } from '@finverity/graphql';
import { environment } from '@finverity/platform-app/env/environment';

import { NavigationMenuPermissions } from '../../../identity-access';
import {
  NavigationSidebarBottomMenuItem,
  NavigationSidebarMenuItem,
  NavigationSidebarMenuItemIdentifier,
} from '../types';

const FUNDER_MAKER_APPROVAL_MENU_ITEM_BASE: Omit<NavigationSidebarMenuItem, 'identifier'> = {
  title: 'shell.navigationMenu.makerApproval',
  titleForHeader: 'instrument-approval.pages.funderApprovalMaker.pageTitle',
  route: 'funder-approval-maker',
};

const FUNDER_CHECKER_APPROVAL_MENU_ITEM_BASE: Omit<NavigationSidebarMenuItem, 'identifier'> = {
  title: 'shell.navigationMenu.checkerApproval',
  titleForHeader: 'instrument-approval.pages.funderApprovalChecker.pageTitle',
  route: 'funder-approval-checker',
};

const FUNDER_APPROVAL_REJECTED_MENU_ITEM_BASE: Omit<NavigationSidebarMenuItem, 'identifier'> = {
  title: 'shell.navigationMenu.rejected',
  titleForHeader: 'instrument-approval.pages.funderApprovalRejected.pageTitle',
  route: 'funder-approval-rejected',
  iconName: 'fvIconNavMenuRejected',
};

const READY_FOR_FUNDING_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.ReadyForFunding,
  title: 'shell.navigationMenu.readyForFunding',
  route: '/ready-for-funding',
  permission: NavigationMenuPermissions.ReadyForFunding,
};

const DISBURSEMENT_CONFIRMATION_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.DisbursementConfirmation,
  title: 'shell.navigationMenu.disbursementConfirmation',
  route: '/disbursement-confirmation',
};

const DISBURSEMENT_FILES_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.DisbursementFiles,
  title: 'shell.navigationMenu.disbursementFiles',
  route: '/disbursement-files',
  companyTypes: [CompanyType.PortalAdmin, CompanyType.Funder],
};

/** Repayments section items */
const OUTSTANDING_INSTRUMENTS_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.OutstandingInstruments,
  title: 'shell.navigationMenu.outstandingInstruments',
  route: '/outstanding-instruments',
  iconName: 'fvIconNavMenuOutstandingInstruments',
  permission: NavigationMenuPermissions.ViewOutstandingInstruments,
};

const NPL_STATUS_APPROVALS_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.NplStatusApprovals,
  title: 'shell.navigationMenu.nplStatusApprovals',
  route: '/npl-approvals',
  iconName: 'fvIconNavMenuNplStatusApprovals',
  permission: NavigationMenuPermissions.ViewNplStatusApprovals,
};

const REPAYMENTS_MENU_ITEM: NavigationSidebarMenuItem = {
  identifier: NavigationSidebarMenuItemIdentifier.Repayments,
  title: 'shell.navigationMenu.repayments',
  route: '/repayments',
  iconName: 'fvIconNavMenuRepayments',
  permission: NavigationMenuPermissions.ViewRepayments,
};

const REPAYMENTS_MENU_ITEM_BASE: NavigationSidebarMenuItem = {
  // Distinct identifier from the leaf REPAYMENTS_MENU_ITEM below: the parent group and its child
  // must not share an identifier, otherwise findParentChain() matches the group first and collapses it.
  identifier: NavigationSidebarMenuItemIdentifier.RepaymentsAndReconciliation,
  title: 'shell.navigationMenu.repaymentsAndReconciliation',
};

export const NAVIGATION_SIDEBAR_MENU_ITEMS: NavigationSidebarMenuItem[] = [
  // HOME SECTION
  {
    identifier: NavigationSidebarMenuItemIdentifier.Home,
    title: 'shell.navigationMenu.home',
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-home)',
      iconColorVarName: 'var(--fv-color-sidebar-home)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.Dashboard,
        title: 'shell.navigationMenu.dashboard',
        route: '/dashboard',
        iconName: 'fvIconNavMenuDashboard',
        permission: NavigationMenuPermissions.ViewDashboard,
      },
    ],
  },

  // INSTRUMENT UPLOAD SECTION
  {
    // Distinct identifier from the leaf InstrumentUpload child below: the parent section and its
    // child must not share an identifier, otherwise findParentChain() matches the section first and
    // returns an empty chain, so on a direct load at /instrument-upload the section is never
    // auto-expanded and the active leaf stays hidden (same pattern as REPAYMENTS_MENU_ITEM_BASE).
    identifier: NavigationSidebarMenuItemIdentifier.InstrumentUploadSection,
    title: 'shell.navigationMenu.instrumentUpload',
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-instruments)',
      iconColorVarName: 'var(--fv-color-sidebar-instruments)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.InstrumentUpload,
        title: 'shell.navigationMenu.uploadInstruments',
        route: '/instrument-upload',
        iconName: 'fvIconNavMenuInstrumentUpload',
        permission: NavigationMenuPermissions.ViewInstrumentUpload,
      },
    ],
  },

  // CREDIT NOTES SECTION
  {
    identifier: NavigationSidebarMenuItemIdentifier.CreditNotes,
    title: 'shell.navigationMenu.creditNotes',
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-credit-notes)',
      iconColorVarName: 'var(--fv-color-sidebar-credit-notes)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.AllocatedCreditNotes,
        title: 'shell.navigationMenu.allocatedCreditNotes',
        route: '/allocated-credit-notes',
        iconName: 'fvIconNavMenuAllocatedCreditNotes',
        permission: NavigationMenuPermissions.ViewAllocatedCreditNotes,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.CreditNoteRules,
        title: 'shell.navigationMenu.creditNoteRules',
        iconName: 'fvIconNavMenuCreditNoteRules',
        permission: NavigationMenuPermissions.ViewCreditNoteRules,
        companyTypes: [CompanyType.PortalAdmin],
        route: '/credit-note-rules',
      },
    ],
  },

  // FINANCE REQUESTS SECTION / Funder Approvals (Funder)
  {
    identifier: NavigationSidebarMenuItemIdentifier.FinanceRequestWorkflow,
    title: 'shell.navigationMenu.financeRequests',
    permission: NavigationMenuPermissions.FinanceApprovalFunder,
    companyTypes: [CompanyType.Funder],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-finance-requests)',
      iconColorVarName: 'var(--fv-color-sidebar-finance-requests)',
      withBorderLeft: true,
    },
    children: [
      {
        ...FUNDER_MAKER_APPROVAL_MENU_ITEM_BASE,
        identifier: NavigationSidebarMenuItemIdentifier.FunderApprovalMaker,
        iconName: 'fvIconNavMenuFunderApprovalMaker',
        permission: NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalMaker,
      },
      {
        ...FUNDER_CHECKER_APPROVAL_MENU_ITEM_BASE,
        identifier: NavigationSidebarMenuItemIdentifier.FunderApprovalChecker,
        iconName: 'fvIconNavMenuInstrumentApproval',
        permission: NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalChecker,
      },
      {
        ...FUNDER_APPROVAL_REJECTED_MENU_ITEM_BASE,
        identifier: NavigationSidebarMenuItemIdentifier.FunderApprovalRejected,
        iconName: 'fvIconNavMenuRejected',
        permission: NavigationMenuPermissions.ManageRejectedInstrumentsFunderApproval,
      },
    ],
  },

  // TRANSACTION WORKFLOW (Portal Admin)
  {
    identifier: NavigationSidebarMenuItemIdentifier.TransactionWorkflow,
    title: 'shell.navigationMenu.transactionWorkflow',
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-transaction-workflow)',
      iconColorVarName: 'var(--fv-color-sidebar-transaction-workflow)',
      withBorderLeft: true,
    },
    children: [
      // FUNDER APPROVALS SECTION (Portal Admin)
      {
        identifier: NavigationSidebarMenuItemIdentifier.FunderApprovals,
        title: 'shell.navigationMenu.funderApprovals',
        iconName: 'fvIconNavMenuFunderApprovals',
        permission: NavigationMenuPermissions.ViewFunderApprovalPagesAdmin,
        children: [
          {
            ...FUNDER_MAKER_APPROVAL_MENU_ITEM_BASE,
            identifier: NavigationSidebarMenuItemIdentifier.PortalAdminFunderApprovalMaker,
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalMakerAdmin,
            companyTypes: [CompanyType.PortalAdmin],
          },
          {
            ...FUNDER_CHECKER_APPROVAL_MENU_ITEM_BASE,
            identifier: NavigationSidebarMenuItemIdentifier.PortalAdminFunderApprovalChecker,
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsFunderApprovalCheckerAdmin,
            companyTypes: [CompanyType.PortalAdmin],
          },
          {
            ...FUNDER_APPROVAL_REJECTED_MENU_ITEM_BASE,
            iconName: undefined,
            identifier: NavigationSidebarMenuItemIdentifier.PortalAdminFunderApprovalRejected,
            permission: NavigationMenuPermissions.ManageRejectedInstrumentsFunderApprovalAdmin,
            companyTypes: [CompanyType.PortalAdmin],
          },
        ],
      },
      // DISBURSEMENTS SECTION (Portal Admin)
      {
        identifier: NavigationSidebarMenuItemIdentifier.Disbursements,
        title: 'shell.navigationMenu.disbursements',
        companyTypes: [CompanyType.PortalAdmin],
        iconName: 'fvIconNavMenuDisbursements',
        permission: NavigationMenuPermissions.ViewDisbursementsSectionAdmin,
        children: [
          { ...READY_FOR_FUNDING_MENU_ITEM, titleForHeader: 'shell.navigationMenu.disbursements' },
          {
            ...DISBURSEMENT_CONFIRMATION_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.disbursements',
            permission: NavigationMenuPermissions.InstrumentDisbursementsAdmin,
          },
          {
            ...DISBURSEMENT_FILES_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.disbursements',
            permission: NavigationMenuPermissions.ViewDisbursementFilesAdmin,
          },
        ],
      },
      // REPAYMENTS SECTION (Portal Admin)
      {
        ...REPAYMENTS_MENU_ITEM_BASE,
        iconName: 'fvIconNavMenuFinanceApproval',
        children: [
          {
            ...OUTSTANDING_INSTRUMENTS_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.repaymentsAndReconciliation',
            iconName: undefined,
          },
          {
            ...NPL_STATUS_APPROVALS_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.repaymentsAndReconciliation',
            iconName: undefined,
          },
          {
            ...REPAYMENTS_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.repaymentsAndReconciliation',
            iconName: undefined,
          },
        ],
      },
    ],
  },

  // DISBURSEMENTS (Funder)
  {
    identifier: NavigationSidebarMenuItemIdentifier.Disbursements,
    title: 'shell.navigationMenu.disbursements',
    companyTypes: [CompanyType.Funder],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-disbursement)',
      iconColorVarName: 'var(--fv-color-sidebar-disbursement)',
      withBorderLeft: true,
    },
    children: [
      {
        ...READY_FOR_FUNDING_MENU_ITEM,
        iconName: 'fvIconNavMenuFunderReadyForFunding',
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.Disbursement,
        title: 'shell.navigationMenu.disbursement',
        iconName: 'fvIconNavMenuDisbursements',
        permission: NavigationMenuPermissions.ViewDisbursementsSectionFunder,
        companyTypes: [CompanyType.Funder],
        children: [
          {
            ...DISBURSEMENT_CONFIRMATION_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.disbursement',
            permission: NavigationMenuPermissions.InstrumentDisbursementsFunder,
          },
          {
            ...DISBURSEMENT_FILES_MENU_ITEM,
            titleForHeader: 'shell.navigationMenu.disbursement',
            permission: NavigationMenuPermissions.ViewDisbursementFilesFunder,
          },
        ],
      },
    ],
  },

  // PAYER APPROVAL SECTION (Customer)
  {
    identifier: NavigationSidebarMenuItemIdentifier.PayerApproval,
    title: 'shell.navigationMenu.payerApproval',
    companyTypes: [CompanyType.Customer],
    permission: NavigationMenuPermissions.InstrumentApproval,
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-payer-approval)',
      iconColorVarName: 'var(--fv-color-sidebar-payer-approval)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.PayerApprovalMaker,
        title: 'shell.navigationMenu.makerApproval',
        titleForHeader: 'instrument-approval.pages.payerApprovalMaker.pageTitle',
        route: 'payer-approval-maker',
        iconName: 'fvIconNavMenuPayerApprovalMaker',
        permission: NavigationMenuPermissions.ApproveRejectInstrumentsPayerApprovalMaker,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.PayerApprovalChecker,
        title: 'shell.navigationMenu.checkerApproval',
        titleForHeader: 'instrument-approval.pages.payerApprovalChecker.pageTitle',
        route: 'payer-approval-checker',
        iconName: 'fvIconNavMenuPayerApprovalChecker',
        permission: NavigationMenuPermissions.ApproveRejectInstrumentsPayerApprovalChecker,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.PayerApprovalRejected,
        title: 'shell.navigationMenu.rejected',
        titleForHeader: 'instrument-approval.pages.payerApprovalRejected.pageTitle',
        route: 'payer-approval-rejected',
        iconName: 'fvIconNavMenuPayerApprovalRejected',
        permission: NavigationMenuPermissions.ManageRejectedInstrumentsPayerApproval,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.MarkAsPaidWithoutFinancing,
        title: 'shell.navigationMenu.paidWithoutFinance',
        route: 'mark-as-paid-without-financing',
        iconName: 'fvIconNavMenuMarkAsPaidWithoutFinancing',
        permission: NavigationMenuPermissions.MarkAsPaidWithoutFinancing,
      },
    ],
  },

  // REQUEST FINANCE SECTION (Customer)
  {
    identifier: NavigationSidebarMenuItemIdentifier.RequestFinance,
    title: 'shell.navigationMenu.requestFinance',
    companyTypes: [CompanyType.Customer],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-request-finance)',
      iconColorVarName: 'var(--fv-color-sidebar-request-finance)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.RequestAsSeller,
        title: 'shell.navigationMenu.requestAsSeller',
        iconName: 'fvIconNavMenuRequestAsSeller',
        permission: NavigationMenuPermissions.FinanceRequest,
        children: [
          {
            identifier: NavigationSidebarMenuItemIdentifier.SellerFinanceRequestMaker,
            title: 'shell.navigationMenu.submitFinanceRequestMaker',
            titleForHeader: 'instrument-approval.pages.sellerFinanceMaker.pageTitle',
            route: 'seller-finance-request-maker',
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsSellerFinanceRequestMaker,
          },
          {
            identifier: NavigationSidebarMenuItemIdentifier.SellerFinanceRequestChecker,
            title: 'shell.navigationMenu.approveFinanceRequestChecker',
            titleForHeader: 'instrument-approval.pages.sellerFinanceChecker.pageTitle',
            route: 'seller-finance-request-checker',
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsSellerFinanceRequestChecker,
          },
          {
            identifier: NavigationSidebarMenuItemIdentifier.SellerFinanceRequestRejected,
            title: 'shell.navigationMenu.rejected',
            titleForHeader: 'instrument-approval.pages.sellerFinanceRejected.pageTitle',
            route: 'seller-finance-request-rejected',
            permission: NavigationMenuPermissions.ManageRejectedInstrumentsSellerFinanceRequest,
          },
        ],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.RequestAsPayer,
        title: 'shell.navigationMenu.requestAsPayer',
        iconName: 'fvIconNavMenuFinanceRequest',
        permission: NavigationMenuPermissions.FinanceRequest,
        children: [
          {
            identifier: NavigationSidebarMenuItemIdentifier.PayerFinanceRequestMaker,
            title: 'shell.navigationMenu.submitFinanceRequestMaker',
            titleForHeader: 'instrument-approval.pages.payerFinanceMaker.pageTitle',
            route: 'payer-finance-request-maker',
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsPayerFinanceRequestMaker,
          },
          {
            identifier: NavigationSidebarMenuItemIdentifier.PayerFinanceRequestChecker,
            title: 'shell.navigationMenu.approveFinanceRequestChecker',
            titleForHeader: 'instrument-approval.pages.payerFinanceChecker.pageTitle',
            route: 'payer-finance-request-checker',
            permission: NavigationMenuPermissions.ApproveRejectInstrumentsPayerFinanceRequestChecker,
          },
          {
            identifier: NavigationSidebarMenuItemIdentifier.PayerFinanceRequestRejected,
            title: 'shell.navigationMenu.rejected',
            titleForHeader: 'instrument-approval.pages.payerFinanceRejected.pageTitle',
            route: 'payer-finance-request-rejected',
            permission: NavigationMenuPermissions.ManageRejectedInstrumentsPayerFinanceRequest,
          },
        ],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.RejectedByFunder,
        title: 'shell.navigationMenu.rejectedByFunder',
        route: 'funder-approval-rejected',
        iconName: 'fvIconNavMenuRejected',
        permission: NavigationMenuPermissions.ManageFunderRejectedInstrumentsCustomer,
        companyTypes: [CompanyType.Customer, CompanyType.Funder],
      },
    ],
  },

  // REPAYMENTS SECTION (Customer, Funder)
  {
    ...REPAYMENTS_MENU_ITEM_BASE,
    companyTypes: [CompanyType.Customer, CompanyType.Funder],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-repayments)',
      iconColorVarName: 'var(--fv-color-sidebar-repayments)',
      withBorderLeft: true,
    },
    children: [
      OUTSTANDING_INSTRUMENTS_MENU_ITEM,
      { ...NPL_STATUS_APPROVALS_MENU_ITEM, companyTypes: [CompanyType.Funder] },
      REPAYMENTS_MENU_ITEM,
    ],
  },

  // PAYMENTS SECTION
  {
    identifier: NavigationSidebarMenuItemIdentifier.Payments,
    title: 'shell.navigationMenu.payments',
    companyTypes: [CompanyType.Customer],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-payments)',
      iconColorVarName: 'var(--fv-color-sidebar-payments)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.IncomingPayments,
        title: 'shell.navigationMenu.incomingPayments',
        route: '/incoming-payments',
        iconName: 'fvIconNavMenuCustomerIncomingPayments',
        permission: NavigationMenuPermissions.ViewIncomingPayments,
        companyTypes: [CompanyType.Customer],
      },
    ],
  },

  // PROGRAMMES AND DEALS
  {
    identifier: NavigationSidebarMenuItemIdentifier.ProgrammesAndDeals,
    title: 'shell.navigationMenu.programmesAndDeals',
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-programmes-and-deals)',
      iconColorVarName: 'var(--fv-color-sidebar-programmes-and-deals)',
      withBorderLeft: true,
    },
    permission: NavigationMenuPermissions.ViewProgrammesAndDeals,
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.Programmes,
        iconName: 'fvIconNavMenuProgrammesAndDeals',
        title: 'shell.navigationMenu.programmes',
        route: '/programmes',
        permission: NavigationMenuPermissions.ViewProgrammes,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.Deals,
        title: 'shell.navigationMenu.deals',
        iconName: 'fvIconNavMenuDeals',
        route: '/deals',
        matcher: routeUrl => routeUrl.startsWith('/deal/'),
        permission: NavigationMenuPermissions.ViewDeals,
      },
    ],
  },

  // COMPANY ONBOARDING
  {
    identifier: NavigationSidebarMenuItemIdentifier.CompanyOnboarding,
    title: 'shell.navigationMenu.companyOnboarding',
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-company-onboarding)',
      iconColorVarName: 'var(--fv-color-sidebar-company-onboarding)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.Companies,
        title: 'shell.navigationMenu.companies',
        route: '/companies',
        matcher: routeUrl => routeUrl.startsWith('/company-onboarding/'),
        iconName: 'fvIconNavMenuCompanies',
        permission: NavigationMenuPermissions.ViewCompanies,
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.MasterRegistrationForm,
        title: 'shell.navigationMenu.masterRegistrationForm',
        companyTypes: [CompanyType.PortalAdmin],
        route: '/master-registration-form',
        iconName: 'fvIconNavMenuOnboardingForm',
        permission: NavigationMenuPermissions.ManageOnboardingFormAndRules,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.SelfOnboarding,
        title: 'shell.navigationMenu.selfOnboarding',
        route: '/self-onboarding',
        iconName: 'fvIconNavMenuSelfOnboarding',
        permission: NavigationMenuPermissions.ViewSelfOnboarding,
        companyTypes: [CompanyType.PortalAdmin],
      },
    ],
  },

  // CONTRACTS
  {
    identifier: NavigationSidebarMenuItemIdentifier.DigitalContracts,
    title: 'shell.navigationMenu.digitalContracts',
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-contracts)',
      iconColorVarName: 'var(--fv-color-sidebar-contracts)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.ContractTemplates,
        title: 'shell.navigationMenu.contractTemplates',
        route: '/contract-templates',
        // Bespoke name (not a generic '@tui.*') so a white-label build can override this sidebar icon
        // in isolation via white-labeling/<tenant>/tenant-icon-overrides.ts — that override is keyed by
        // this name, so renaming here silently breaks it; other builds fall back to phosphor file-text
        // via FVICON_TO_PHOSPHOR.
        iconName: 'fvIconNavMenuDigitalContracts',
        permission: NavigationMenuPermissions.ViewContractTemplates,
        companyTypes: [CompanyType.PortalAdmin],
      },
    ],
  },

  // PRODUCT MANAGEMENT
  {
    identifier: NavigationSidebarMenuItemIdentifier.ProductManagement,
    title: 'shell.navigationMenu.productManagement',
    permission: NavigationMenuPermissions.ProductsManagement,
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-product-management)',
      iconColorVarName: 'var(--fv-color-sidebar-product-management)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.ProductGroups,
        title: 'shell.navigationMenu.productGroups',
        route: '/product-group-management',
        iconName: 'fvIconNavMenuProducts',
        permission: NavigationMenuPermissions.ViewProductGroups,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.ProductStructures,
        title: 'shell.navigationMenu.productStructures',
        route: '/product-structure-management',
        iconName: 'fvIconNavMenuProductStructures',
        permission: NavigationMenuPermissions.ViewProductStructures,
      },
    ],
  },

  // FX AND FLOATING RATES
  {
    identifier: NavigationSidebarMenuItemIdentifier.FxAndFloatingRates,
    title: 'shell.navigationMenu.fxAndFloatingRates',
    permission: NavigationMenuPermissions.FxAndFloatingRates,
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-fx-and-floating-rates)',
      iconColorVarName: 'var(--fv-color-sidebar-fx-and-floating-rates)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.FloatingRates,
        title: 'shell.navigationMenu.floatingRates',
        route: '/floating-rate-management',
        iconName: 'fvIconNavMenuFloatingRates',
        permission: NavigationMenuPermissions.ViewFloatingRates,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.FXRates,
        title: 'shell.navigationMenu.fxRates',
        route: '/fx-rate-management',
        iconName: 'fvIconNavMenuFxRates',
        permission: NavigationMenuPermissions.ViewFXRates,
      },
    ],
  },
  {
    identifier: NavigationSidebarMenuItemIdentifier.AdditionalSettings,
    title: 'shell.navigationMenu.additionalSettings',
    companyTypes: [CompanyType.PortalAdmin],
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-additional-settings)',
      iconColorVarName: 'var(--fv-color-sidebar-additional-settings)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.BankHolidayRules,
        title: 'shell.navigationMenu.bankHolidayRules',
        iconName: 'fvIconBankHolidayRules',
        permission: NavigationMenuPermissions.ViewBankHolidayRules,
        route: '/bank-holiday-rules',
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.NotificationTemplates,
        title: 'shell.navigationMenu.notificationTemplates',
        iconName: 'fvIconNavMenuNotificationTemplates',
        permission: NavigationMenuPermissions.ViewNotificationTemplates,
        route: '/notification-templates',
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.WebhookManagement,
        title: 'shell.navigationMenu.webhookManagement',
        route: '/webhook-management',
        iconName: 'fvIconNavMenuWebhookManagement',
        permission: NavigationMenuPermissions.ViewWebhookManagement,
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.SystemHealthNotifications,
        title: 'shell.navigationMenu.systemHealthNotifications',
        route: '/system-health-notifications',
        iconName: 'fvIconNavMenuSystemHealthNotifications',
        permission: NavigationMenuPermissions.ViewSystemHealthNotifications,
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.CustomFields,
        title: 'shell.navigationMenu.customFields',
        route: '/custom-fields-management',
        iconName: 'fvIconNavMenuCustomFields',
        permission: NavigationMenuPermissions.ViewCustomFields,
        companyTypes: [CompanyType.PortalAdmin],
      },
    ],
  },

  // My Organisation section
  {
    identifier: NavigationSidebarMenuItemIdentifier.MyOrganisation,
    title: 'shell.navigationMenu.myOrganisation',
    styles: {
      colorVarName: 'var(--fv-color-sidebar-line-my-organisation)',
      iconColorVarName: 'var(--fv-color-sidebar-my-organisation)',
      withBorderLeft: true,
    },
    children: [
      {
        identifier: NavigationSidebarMenuItemIdentifier.CustomerCompanyDetails,
        title: 'shell.navigationMenu.companyDetails',
        route: '/company-details',
        iconName: 'fvIconCompanyDetails',
        permission: NavigationMenuPermissions.CustomerViewCompanyDetails,
        companyTypes: [CompanyType.Customer],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.CompanyNotifications,
        title: 'shell.navigationMenu.companyNotifications',
        titleForHeader: 'shell.navigationMenu.companyNotificationSettings',
        route: '/company-notifications',
        iconName: 'fvIconNavMenuNotificationSettings',
        permission: NavigationMenuPermissions.ViewCompanyNotifications,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.UserManagement,
        title: 'shell.navigationMenu.userManagement',
        route: '/user-management',
        iconName: 'fvIconNavMenuUserManagement',
        permission: NavigationMenuPermissions.ViewUserManagement,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.TransactionLimits,
        title: 'shell.navigationMenu.transactionLimits',
        route: '/transaction-limits',
        iconName: 'fvIconNavMenuTransactionLimits',
        permission: NavigationMenuPermissions.ViewTransactionLimits,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.ServiceAccountManagement,
        title: 'shell.navigationMenu.serviceAccountManagement',
        route: '/service-account-management',
        iconName: 'fvIconNavMenuServiceAccountManagement',
        permission: NavigationMenuPermissions.ViewServiceAccountManagement,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.CompanySecuritySettings,
        title: 'shell.navigationMenu.companySecuritySettings',
        route: '/company-security-settings',
        iconName: 'fvIconNavMenuCompanySecuritySettings',
        permission: NavigationMenuPermissions.ViewCompanySecuritySettings,
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.AuditTrail,
        title: 'shell.navigationMenu.auditTrail',
        route: '/audit-trail',
        iconName: 'fvIconNavMenuAuditTrail',
        permission: NavigationMenuPermissions.ViewAuditTrail,
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.ScheduledJobs,
        title: 'shell.navigationMenu.scheduledJobs',
        route: '/scheduled-jobs',
        iconName: 'fvIconNavMenuScheduledJobs',
        permission: NavigationMenuPermissions.ViewScheduledJobs,
        companyTypes: [CompanyType.PortalAdmin],
      },
      {
        identifier: NavigationSidebarMenuItemIdentifier.CompanySsoSettings,
        title: 'shell.navigationMenu.companySsoSettings',
        route: '/sso/company-settings',
        iconName: 'fvIconSso',
        permission: NavigationMenuPermissions.ViewCompanySsoSettings,
      },
    ],
  },
];

export const NAVIGATION_SIDEBAR_BOTTOM_MENU_ITEMS: NavigationSidebarBottomMenuItem[] = [
  {
    identifier: NavigationSidebarMenuItemIdentifier.ReportingModule,
    title: 'shell.navigationMenu.reportingModule',
    href: environment.platform.reportingModuleLink,
    iconName: 'fvIconNavMenuReportingModule',
    companyTypes: [CompanyType.PortalAdmin, CompanyType.Funder],
  },
  {
    identifier: NavigationSidebarMenuItemIdentifier.Changelog,
    title: 'shell.navigationMenu.changelog',
    href: 'https://releases.finverity.com/',
    iconName: 'fvIconNavMenuChangelog',
    companyTypes: [CompanyType.PortalAdmin, CompanyType.Funder],
  },
  {
    identifier: NavigationSidebarMenuItemIdentifier.Help,
    title: 'shell.navigationMenu.help',
    href: environment.platform.helpLink,
    iconName: 'fvIconNavMenuHelp',
    companyTypes: [CompanyType.PortalAdmin, CompanyType.Funder],
  },
];
